const yellowBackgrounds = new Set([
  'rgb(251, 190, 9)',
  'rgb(251, 192, 9)',
  'rgb(255, 193, 7)',
]);

const blackBackgrounds = new Set([
  'rgb(0, 0, 0)',
  'rgb(47, 47, 47)',
  'rgb(51, 51, 51)',
]);

const transparentBackgrounds = new Set([
  'rgba(0, 0, 0, 0)',
  'transparent',
]);

const blackBorders = new Set([
  'rgb(0, 0, 0)',
  'rgb(47, 47, 47)',
  'rgb(51, 51, 51)',
]);

const actionSelector = 'a, button, input[type="button"], input[type="submit"], input[type="reset"]';
const excludedSelector = [
  '.button-unstyled',
  '.yx-product-card__favorite',
  '.yx-product-card__info',
  '.yx-product-card__button',
  '.yx-product-card__media-link',
  '.yx-site-header__discount-icon',
  '.yx-home__hero-tab',
  '.yx-home__stripe-dots',
  '.yx-site-footer__service-strip',
  '.yx-site-footer__trustpilot',
  '.yx-home__review-actions button',
  '.yx-home__system-actions button',
  '.yx-home__article-arrows button',
  '.main-product__sizes button',
  '.main-product__swatches button',
  '.main-product__thumb',
  '.main-product__stage-arrow',
  '.main-product__story-arrows button',
  '.main-product__related-arrows button',
  '.main-collection__mobile-more a',
  '.yx-search__more div > a',
  '[data-yx-quick-buy-value]',
  '.variant-option__button-label',
  '.yx-grid-promo',
  '.yx-collection__grid-promo',
  '[class*="arrow"]',
  '[class*="chevron"]',
].join(',');

function hasActionLabel(element) {
  if (element instanceof HTMLInputElement) return element.value.trim().length > 1;
  return element.textContent?.trim().length > 1;
}

function ctaStyles(element) {
  if (!(element instanceof HTMLElement) || element.matches(excludedSelector) || !hasActionLabel(element)) return null;

  const box = element.getBoundingClientRect();
  if (box.width < 64 || box.height < 28) return null;
  return getComputedStyle(element);
}

function ctaClass(element) {
  const styles = ctaStyles(element);
  if (!styles) return null;
  if (yellowBackgrounds.has(styles.backgroundColor)) return 'yx-yellow-cta';
  if (blackBackgrounds.has(styles.backgroundColor)) return 'yx-black-cta';
  if ((transparentBackgrounds.has(styles.backgroundColor) || styles.backgroundColor === 'rgb(255, 255, 255)')
    && blackBorders.has(styles.borderColor)
    && Number.parseFloat(styles.borderTopWidth) > 0) return 'yx-outline-cta';
  return null;
}

function yellowCtaPlateIn(action) {
  return [...action.querySelectorAll('*')].find((element) => {
    const styles = ctaStyles(element);
    return styles && yellowBackgrounds.has(styles.backgroundColor);
  });
}

function markYellowCtas(scopes) {
  const elements = new Set();
  scopes.forEach((scope) => {
    if (scope instanceof Element && scope.matches(actionSelector)) elements.add(scope);
    scope.querySelectorAll?.(actionSelector).forEach((element) => elements.add(element));
  });

  // Read all geometry/styles before applying markers. These classes control
  // colour states only, so batching avoids a layout flush between controls.
  const markers = new Map();
  elements.forEach((element) => {
    if (markers.has(element) || element.matches('.yx-yellow-cta, .yx-black-cta, .yx-outline-cta, .yx-yellow-cta-owner')) return;

    const marker = ctaClass(element);
    if (marker) {
      markers.set(element, marker);
      return;
    }
    const plate = yellowCtaPlateIn(element);
    if (!plate) return;
    markers.set(element, 'yx-yellow-cta-owner');
    markers.set(plate, 'yx-yellow-cta');
  });
  markers.forEach((marker, element) => element.classList.add(marker));
}

let scanQueued = false;
const pendingScopes = new Set();

function queueYellowCtaScan(scope = document) {
  pendingScopes.add(scope);
  if (scanQueued) return;
  scanQueued = true;
  requestAnimationFrame(() => {
    scanQueued = false;
    markYellowCtas(pendingScopes);
    pendingScopes.clear();
  });
}

let ctaVisibilityObserver = null;

function observeYellowCtas(scope = document) {
  if (!ctaVisibilityObserver) {
    queueYellowCtaScan(scope);
    return;
  }
  const observeAction = (action) => {
    if (!action.matches('.yx-yellow-cta, .yx-black-cta, .yx-outline-cta, .yx-yellow-cta-owner')) {
      ctaVisibilityObserver.observe(action);
    }
  };
  if (scope instanceof Element && scope.matches(actionSelector)) observeAction(scope);
  scope.querySelectorAll?.(actionSelector).forEach(observeAction);
}

function initYellowCtaHover() {
  if ('IntersectionObserver' in window) {
    ctaVisibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        ctaVisibilityObserver.unobserve(entry.target);
        queueYellowCtaScan(entry.target);
      });
    }, { rootMargin: '512px 0px' });
  }
  observeYellowCtas();

  // Prepare offscreen keyboard targets before the browser moves focus. This
  // runs once on keyboard intent, retaining preclassified hover/focus colours.
  const prepareKeyboardCtas = (event) => {
    if (event.key !== 'Tab') return;
    markYellowCtas(new Set([document]));
    document.removeEventListener('keydown', prepareKeyboardCtas, true);
  };
  document.addEventListener('keydown', prepareKeyboardCtas, true);

  // Drawers and quick-buy panels can reveal their CTAs after the first scan.
  // Mark the actual textual control as it becomes interactive as well.
  const queueActionScope = (event) => {
    if (!(event.target instanceof Element)) return;
    const action = event.target.closest(actionSelector);
    if (action) queueYellowCtaScan(action);
  };

  document.addEventListener('pointerover', queueActionScope, { passive: true });
  document.addEventListener('focusin', queueActionScope);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) observeYellowCtas(node);
      });
    });
  }).observe(document.body, {
    childList: true,
    subtree: true,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initYellowCtaHover, { once: true });
} else {
  initYellowCtaHover();
}
